import chevronDownIcon from "@tabler/icons/outline/chevron-down.svg";
import chevronLeftIcon from "@tabler/icons/outline/chevron-left.svg";
import chevronRightIcon from "@tabler/icons/outline/chevron-right.svg";
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";

import DropdownMenu, { type Menu } from "@/components/dropdown-menu";
import { HStack, Icon } from "@/components/ui";
import { useRect } from "@/hooks";
import { cn } from "@/utils/cn";
import { shortNumberFormat } from "@/utils/numbers";

export interface Item {
  label: string;
  count?: number;
  action?: () => void;
  triggerActionOnOpen?: boolean;
  isActiveCheck?: () => boolean;
  showIndicator?: boolean;
  name: string;
  children?: {
    label: string;
    parentLabel: string;
    action: () => void;
    isActiveCheck?: () => boolean;
    name: string;
    iconSrc: string;
  }[];
}

export interface TabsProps {
  items: Item[];
  activeItem: string;
  disabled?: boolean;
  isLoading?: boolean;
  variant?: "underline" | "pill";
  placeholder?: {
    count: number;
    label: string;
  };
}

/** Tabs with a sliding active state. */
const Tabs = ({
  items,
  activeItem,
  disabled,
  isLoading,
  variant = "underline",
  placeholder,
}: TabsProps) => {
  const [scrollLeft, setScrollLeft] = useState(0);

  const listRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const selectedItem = useRef<HTMLButtonElement>(null);
  const initialized = useRef(false);
  const lastScrolledActiveItem = useRef<string | null>(null);
  const [selectedItemRect, setSelectedItemRect] = useState<DOMRect | null>(
    null,
  );

  const listRect = useRect(listRef);

  const isScrollable =
    (listRef.current?.scrollWidth || 0) > (listRect?.width || 0);

  const left = (selectedItemRect?.left || 0) - (listRect?.left || 0);
  const width = selectedItemRect?.width || 0;

  const canScrollLeft = scrollLeft > 0 && isScrollable;
  const canScrollRight =
    scrollLeft + (listRect?.width || 0) <
    (listRef.current?.scrollWidth || 0) - 10;

  const getMenuFromChildren = (item: Item): Menu =>
    item.children!.map((subItem) => {
      return {
        ...subItem,
        text: subItem.label,
        icon: subItem.iconSrc,
        active: subItem.isActiveCheck
          ? subItem.isActiveCheck()
          : subItem.name === activeItem,
      };
    });

  const renderLabel = (item: Item) => {
    return (
      <>
        {item.label}
        {(item.count || 0) > 0 && <> ({shortNumberFormat(item.count, 99)})</>}
        {item.showIndicator && (
          <div className="ml-1 size-2 rounded-full bg-accent-pink-300" />
        )}
      </>
    );
  };

  const tabStyle = (isActive: boolean) => {
    if (variant === "pill") {
      return cn(
        "flex items-center justify-center rounded-full border-2 px-4 py-1 text-center text-sm font-medium whitespace-nowrap transition-all focus:ring-2 focus:outline-hidden disabled:pointer-events-none disabled:opacity-50",
        {
          "border-transparent bg-primary-500 text-gray-100 hover:bg-primary-400 focus:bg-primary-500 focus:ring-primary-300 disabled:hover:bg-primary-500 dark:hover:bg-primary-600 dark:disabled:hover:bg-primary-500":
            isActive,
          "border-gray-300 bg-transparent text-gray-900 hover:border-gray-500 focus:border-gray-300 focus:ring-gray-500 dark:border-gray-800 dark:text-gray-100 dark:hover:border-gray-700 dark:focus:border-gray-800 dark:focus:ring-gray-700":
            !isActive,
          "animate-pulse text-gray-300! dark:text-gray-800!": isLoading,
        },
      );
    }

    return cn(
      "flex flex-1 items-center justify-center px-4 py-2 pt-3 text-center text-sm whitespace-nowrap hover:text-gray-800 disabled:pointer-events-none disabled:opacity-50 dark:hover:text-gray-50",
      {
        "font-medium text-muted-foreground": !isActive,
        "font-bold text-gray-900 dark:text-gray-100": isActive,
        "animate-pulse text-gray-300! dark:text-gray-800!": isLoading,
      },
    );
  };

  const scroll = (direction: "left" | "right") => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({
        left: direction === "left" ? -200 : 200,
        behavior: "smooth",
      });
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      const { scrollLeft } = scrollRef.current!;
      setScrollLeft(scrollLeft);
    };

    const currentScrollRef = scrollRef.current;

    if (currentScrollRef && isScrollable) {
      currentScrollRef.addEventListener("scroll", handleScroll);
    }
    return () => {
      if (currentScrollRef && isScrollable) {
        currentScrollRef.removeEventListener("scroll", handleScroll);
      }
    };
  }, [listRect?.width, isScrollable]);

  useLayoutEffect(() => {
    const element = selectedItem.current;
    if (!element) {
      setSelectedItemRect(null);
      return;
    }

    const update = () => setSelectedItemRect(element.getBoundingClientRect());
    update();

    const win = element.ownerDocument?.defaultView;
    win?.addEventListener("resize", update);
    win?.addEventListener("scroll", update, true);

    return () => {
      win?.removeEventListener("resize", update);
      win?.removeEventListener("scroll", update, true);
    };
  }, [activeItem, listRect?.width, scrollLeft, items]);

  useEffect(() => {
    if (!scrollRef.current || !selectedItemRect) {
      return;
    }

    if (lastScrolledActiveItem.current === activeItem && initialized.current) {
      return;
    }
    lastScrolledActiveItem.current = activeItem;

    const scrollRect = scrollRef.current.getBoundingClientRect();
    const isFullyVisible =
      selectedItemRect.left >= scrollRect.left &&
      selectedItemRect.right <= scrollRect.right;

    if (!isFullyVisible) {
      const offset = selectedItemRect.left - scrollRect.left;
      scrollRef.current.scrollTo({
        left: scrollRef.current.scrollLeft + offset,
        behavior: initialized.current ? "smooth" : "auto",
      });
    }

    initialized.current = true;
  }, [activeItem, selectedItemRect, listRect, scrollLeft]);

  return (
    <div className="relative" data-testid="tabs">
      <div
        className={cn(
          "relative scrollbar-hidden overflow-x-auto scroll-smooth",
          variant === "underline" ? "pb-[3px]" : "py-1",
        )}
        ref={scrollRef}
      >
        <div
          className={cn("flex", {
            "gap-2 px-4": variant === "pill",
          })}
          role="tablist"
          ref={listRef}
          data-testid="tabs-list"
        >
          {isLoading &&
            placeholder &&
            Array.from({ length: placeholder.count }).map((_, index) => (
              <button
                key={index}
                className={tabStyle(false)}
                disabled={isLoading}
                aria-disabled={isLoading}
              >
                {placeholder.label}
              </button>
            ))}
          {!isLoading &&
            items.map((item) => {
              const isActive =
                item.isActiveCheck?.() || item.name === activeItem;
              if (!item.children)
                return (
                  <button
                    key={item.name}
                    disabled={disabled}
                    className={tabStyle(isActive)}
                    ref={isActive ? selectedItem : null}
                    onClick={() => item.action?.()}
                    role="tab"
                    aria-disabled={disabled}
                    aria-selected={isActive ? "true" : "false"}
                    tabIndex={isActive ? 0 : -1}
                  >
                    {renderLabel(item)}
                  </button>
                );

              const activeSubitem = item.children.find((subItem) => {
                return subItem.isActiveCheck
                  ? subItem.isActiveCheck()
                  : subItem.name === activeItem;
              });
              const parentName = activeSubitem ? (
                variant === "pill" ? (
                  <>
                    <span>{item.label}</span>
                    <span className="opacity-60" aria-hidden="true">
                      |
                    </span>
                    <span>{activeSubitem.label}</span>
                  </>
                ) : (
                  activeSubitem.parentLabel
                )
              ) : (
                renderLabel(item)
              );
              return (
                <DropdownMenu
                  items={getMenuFromChildren(item)}
                  placement="bottom"
                  width="medium"
                  disabled={disabled}
                  onOpen={() => {
                    if (item.triggerActionOnOpen ?? true) {
                      item.action?.();
                    }
                  }}
                  key={`${item.name}-dropdown`}
                  containerClassName={
                    variant === "underline" ? "flex-1" : undefined
                  }
                  dropdownClassName="max-h-[200px] overflow-y-auto"
                >
                  <button
                    ref={activeSubitem ? selectedItem : null}
                    className={tabStyle(!!activeSubitem)}
                    disabled={disabled}
                    role="tab"
                    aria-disabled={disabled}
                    aria-selected={isActive ? "true" : "false"}
                    tabIndex={isActive ? 0 : -1}
                  >
                    <HStack space={1.5} alignItems="center">
                      {parentName}
                      {!!activeSubitem && (
                        <Icon className="size-5" src={chevronDownIcon} />
                      )}
                    </HStack>
                  </button>
                </DropdownMenu>
              );
            })}
        </div>

        {variant === "underline" && (
          <>
            <div
              className={cn(
                "absolute z-2 h-[3px] w-full bg-primary-200 dark:bg-gray-800",
                { "opacity-50": disabled },
              )}
              style={{
                bottom: 0,
                width: listRef.current?.scrollWidth || "100%",
              }}
            />
            <div
              className={cn(
                "absolute z-2 h-[3px] bg-primary-500 transition-all duration-200 dark:bg-primary-400",
                {
                  "transition-none! duration-0!": !initialized.current,
                  "opacity-50": disabled,
                },
              )}
              style={{ left, bottom: 0, width: width }}
            />
          </>
        )}
      </div>

      {canScrollLeft && (
        <button
          className="absolute top-0 left-0 z-1 flex h-full w-20 items-center bg-linear-to-r from-white from-50% to-transparent dark:from-gray-900"
          onClick={() => scroll("left")}
          data-testid="tabs-scroll-left"
        >
          <Icon src={chevronLeftIcon} className="ml-4 dark:text-white" />
        </button>
      )}
      {canScrollRight && (
        <button
          className="absolute top-0 right-0 z-1 flex h-full w-20 items-center justify-end bg-linear-to-l from-white from-50% to-transparent dark:from-gray-900"
          onClick={() => scroll("right")}
          data-testid="tabs-scroll-right"
        >
          <Icon src={chevronRightIcon} className="mr-4 dark:text-white" />
        </button>
      )}
    </div>
  );
};

export default Tabs;
